import React from "react"

interface JsonCodeEditorProps {
  value: string
  onChange?: (val: string) => void
  readOnly?: boolean
  minHeight?: string
}

// Tokenise a single JSON line for syntax highlighting
function highlightJson(line: string): React.ReactNode {
  const tokens: React.ReactNode[] = []
  // Regex: key, string value, number, boolean/null, punctuation
  const re = /("(?:\\.|[^"\\])*")\s*:|("(?:\\.|[^"\\])*")|(-?\d+(?:\.\d+)?(?:[eE][+-]?\d+)?)|(\btrue\b|\bfalse\b|\bnull\b)|([[\]{},:])/g
  let last = 0
  let m: RegExpExecArray | null
  let i = 0
  while ((m = re.exec(line)) !== null) {
    if (m.index > last) tokens.push(<span key={`t${i++}`} className="text-gray-400">{line.slice(last, m.index)}</span>)
    if (m[1] !== undefined) {
      // key
      tokens.push(<span key={`t${i++}`} className="text-[#9CDCFE]">{m[1]}</span>)
      // colon after key
      const colon = line.slice(m.index + m[1].length, re.lastIndex)
      tokens.push(<span key={`t${i++}`} className="text-gray-400">{colon}</span>)
    } else if (m[2] !== undefined) {
      tokens.push(<span key={`t${i++}`} className="text-[#CE9178]">{m[2]}</span>)
    } else if (m[3] !== undefined) {
      tokens.push(<span key={`t${i++}`} className="text-[#B5CEA8]">{m[3]}</span>)
    } else if (m[4] !== undefined) {
      tokens.push(<span key={`t${i++}`} className="text-[#569CD6]">{m[4]}</span>)
    } else if (m[5] !== undefined) {
      tokens.push(<span key={`t${i++}`} className="text-gray-500">{m[5]}</span>)
    }
    last = re.lastIndex
  }
  if (last < line.length) tokens.push(<span key={`t${i++}`} className="text-gray-400">{line.slice(last)}</span>)
  return tokens
}

const JsonCodeEditor: React.FC<JsonCodeEditorProps> = ({
  value,
  onChange,
  readOnly = false,
  minHeight = "350px",
}) => {
  const textareaRef = React.useRef<HTMLTextAreaElement>(null)
  const lineNumbersRef = React.useRef<HTMLDivElement>(null)
  const highlightRef = React.useRef<HTMLDivElement>(null)

  const lines = value.split("\n")
  const lineCount = lines.length

  const syncScroll = () => {
    const top = textareaRef.current?.scrollTop ?? 0
    if (lineNumbersRef.current) lineNumbersRef.current.scrollTop = top
    if (highlightRef.current) highlightRef.current.scrollTop = top
  }

  return (
    <div className="flex w-full h-full font-mono text-[13px] leading-relaxed overflow-hidden" style={{ minHeight }}>
      {/* Line numbers */}
      <div
        ref={lineNumbersRef}
        className="shrink-0 select-none overflow-hidden bg-[#1A1A1A] text-right pr-3 pl-3 py-4 text-gray-600 border-r border-gray-700"
        style={{ minHeight, userSelect: "none" }}
        aria-hidden
      >
        {Array.from({ length: lineCount }, (_, i) => (
          <div key={i} className="leading-relaxed h-[1.625rem]">{i + 1}</div>
        ))}
      </div>

      {/* Editor / Highlighted area */}
      <div className="flex-1 relative overflow-auto" style={{ minHeight }}>
        {readOnly ? (
          // Readonly: rich syntax highlighting
          <pre className="py-4 px-4 m-0 whitespace-pre leading-relaxed min-w-max">
            {lines.map((line, i) => (
              <div key={i} className="h-[1.625rem]">{highlightJson(line)}</div>
            ))}
          </pre>
        ) : (
          // Edit mode: simple textarea (no overlay — avoids cursor/scroll bugs)
          <textarea
            ref={textareaRef}
            className="w-full h-full py-4 px-4 text-green-400 bg-transparent focus:outline-none resize-none leading-relaxed"
            style={{ minHeight, lineHeight: "1.625rem" }}
            value={value}
            onChange={(e) => onChange?.(e.target.value)}
            onScroll={syncScroll}
            spellCheck={false}
            autoComplete="off"
            autoCorrect="off"
            autoCapitalize="off"
          />
        )}
      </div>
    </div>
  )
}

export default JsonCodeEditor
